import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from '../../toasts';
import { useNavigate } from 'react-router-dom';
import { FaFileExcel } from 'react-icons/fa';
import BillService from '../../services/bill.service';
import { getError, showCurrency } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import { usePermissions } from '../../hooks/usePermissions';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import ConfirmPayment from '../Bills/ConfirmPayment';
import { Section, StatStrip, DataTable, Pill, RowActionsMenu, Button, ExportModal, buildXlsxExport } from '../../components/EntityList';
import PeriodicBillGroups from './PeriodicBillGroups';
import { splitBillsByType, groupBillsByTemplate } from './billGrouping';
import { useClientSort } from '../../hooks/useClientSort';
import { resolveBillCurrency } from '../Balance/balanceTotals';

function formatDate(date) {
	return date ? new Date(date).toLocaleDateString('es-UY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

/**
 * One pane per Gastos tab. `billType` picks the subset: eventual bills get the
 * flat table, periodic ones the grouped-by-template view.
 *
 * The parent fetches once and passes `rows` down — TabNav keeps both panes
 * addressable by URL, and a delete on one tab must not leave the other stale.
 */
function AssetBills({ asset, billType = 'eventual', rows = [], loading = false, error = '', onRefetch, returnToBase }) {
	const { t } = useLabels();
	const { canCreate } = usePermissions();
	const navigate = useNavigate();
	const [showConfirm, setShowConfirm] = useState(false);
	const [billToDelete, setBillToDelete] = useState(null);
	const [showConfirmPaymentModal, setShowConfirmPaymentModal] = useState(false);
	const [billToConfirm, setBillToConfirm] = useState(null);
	const [exportOpen, setExportOpen] = useState(false);

	const { sortedRows, sortColumn, direction, onSort } = useClientSort(rows, {
		defaultColumn: 'name',
		accessors: { name: (bill) => bill.name },
	});
	const isPeriodicTab = billType === 'periodic';
	// `asset` may arrive unpopulated (ContractDetail passes contract.property).
	const assetId = asset?._id || asset;
	// ContractDetail renders these same tabs, so the editor has to come back to
	// whichever detail page launched it, not always the asset's.
	const returnTo = `${returnToBase || `/AdminScreen/assets/asset/${assetId}`}/bills-${isPeriodicTab ? 'periodic' : 'eventual'}`;

	// Split the already-sorted rows so the eventual tab follows the header sort;
	// periodic groups apply their own order.
	const visibleBills = useMemo(() => {
		const { periodic, eventual } = splitBillsByType(sortedRows);
		return isPeriodicTab ? periodic : eventual;
	}, [sortedRows, isPeriodicTab]);

	const periodicGroups = useMemo(
		() => (isPeriodicTab ? groupBillsByTemplate(visibleBills) : []),
		[isPeriodicTab, visibleBills],
	);

	// A bill may carry its own currency (USD supplier bill on a UYU asset);
	// otherwise it is the asset's.
	const billCurrency = (bill) => resolveBillCurrency({ ...bill, asset: bill.asset && typeof bill.asset === 'object' ? bill.asset : asset });

	// Stats describe the tab the user is looking at, not every bill on the asset.
	// Totals per currency: never add USD and UYU together.
	const stats = useMemo(() => {
		const unpaid = visibleBills.filter((bill) => !bill.paid && !bill.confirmed).length;
		const totalByCurrency = new Map();
		visibleBills.forEach((bill) => {
			const key = billCurrency(bill) || '—';
			totalByCurrency.set(key, (totalByCurrency.get(key) || 0) + (bill.amount || 0));
		});
		return [
			{ label: 'Gastos', value: visibleBills.length },
			{ label: 'Pendientes', value: unpaid },
			{
				label: 'Total',
				value: [...totalByCurrency.entries()].map(([currency, total]) => formatMoney(currency, total)).join(' · ') || '—',
				mono: true,
			},
		];
	}, [visibleBills, asset]);

	const templateHasAlreadyBillsGenerated = (billToCheck) => {
		if (!billToCheck || !billToCheck.billTemplate) return false;
		const targetId = typeof billToCheck.billTemplate === 'object' ? billToCheck.billTemplate._id : billToCheck.billTemplate;
		return rows.some((b) => {
			if (!b || !b.billTemplate) return false;
			const bTemplateId = typeof b.billTemplate === 'object' ? b.billTemplate._id : b.billTemplate;
			return bTemplateId && targetId && bTemplateId === targetId && b._id !== billToCheck._id;
		});
	};

	function deletePaymentHandler(bill) {
		setBillToDelete(bill);
		setShowConfirm(true);
	}

	async function confirmDelete(checkboxValue) {
		if (billToDelete) {
			try {
				await BillService.delete(billToDelete._id, checkboxValue);
				toast.success(t('msg.billDeleted'));
				await onRefetch?.();
			} catch (err) {
				toast.error(getError(err));
			} finally {
				setShowConfirm(false);
				setBillToDelete(null);
			}
		}
	}

	function handleCloseConfirm() {
		setShowConfirm(false);
		setBillToDelete(null);
	}

	function verComprobante(bill) {
		if (bill.comprobante?.fileLink) {
			window.open(bill.comprobante.fileLink, '_blank', 'noopener,noreferrer');
		}
	}

	/**
	 * A generated periodic bill is edited on its own: `scope: 'instance'` stops
	 * the editor rewriting the BillTemplate and backfilling. Its recurring
	 * settings are reached through "Editar plantilla" instead. Eventual bills
	 * have no template, so the full form is right for them.
	 */
	function editBill(bill) {
		navigate(`/AdminScreen/bills/editBill/${bill._id}`, {
			state: { returnTo, scope: isPeriodicTab ? 'instance' : 'full' },
		});
	}

	/** Edits the recurring gasto itself, without touching bills already generated. */
	function editTemplate(group) {
		if (!group.template) return;
		navigate(`/AdminScreen/bills/editTemplate/${group.template._id}`, { state: { returnTo } });
	}

	function newBillHandler() {
		// Seed the type from the tab the user is on, so creating from "Gastos
		// eventuales" doesn't open the form defaulted to Periodico.
		navigate(`/AdminScreen/assets/asset/${assetId}/newBill`, {
			state: { returnTo, billType: isPeriodicTab ? 'Periodico' : 'Eventual' },
		});
	}

	function confirmPayment(bill) {
		setBillToConfirm(bill);
		setShowConfirmPaymentModal(true);
	}

	const closeConfirmPaymentModal = () => {
		setShowConfirmPaymentModal(false);
		setBillToConfirm(null);
	};

	const handlePaymentConfirmed = async () => {
		await onRefetch?.();
		// Without this the modal stays mounted after a successful confirmation.
		closeConfirmPaymentModal();
	};

	// `type`, `periodicity` and the template's active flag are implied by the
	// tab, so the eventual table leaves them out.
	const columns = [
		{ key: 'name', header: t('field.name'), sortable: true, render: (bill) => <span className='tw:font-medium'>{bill.name}</span> },
		{
			key: 'amount',
			header: t('field.amount'),
			className: 'tw:text-right',
			render: (bill) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(billCurrency(bill), bill.amount)}</span>,
		},
		{ key: 'expirationDate', header: t('field.expirationDate'), render: (bill) => formatDate(bill.expirationDate) },
		{
			key: 'paid',
			header: t('field.paid'),
			render: (bill) => (
				<Pill tone={bill.paid || bill.confirmed ? 'ok' : 'warn'}>
					{bill.paid || bill.confirmed ? t('form.yes') : t('form.no')}
				</Pill>
			),
		},
	];

	const exportFields = [
		{ key: 'name', label: t('field.name'), locked: true },
		{ key: 'amount', label: t('field.amount') },
		{ key: 'expirationDate', label: t('field.expirationDate') },
		{ key: 'type', label: t('field.type') },
		{ key: 'periodicity', label: t('field.periodicity') },
		{ key: 'paid', label: t('field.paid') },
	];

	const exportGetters = {
		name: (bill) => bill.name || '',
		amount: (bill) => formatMoney(billCurrency(bill), bill.amount),
		expirationDate: (bill) => formatDate(bill.expirationDate),
		type: (bill) => bill.type || '',
		periodicity: (bill) => bill.periodicity || '',
		paid: (bill) => (bill.paid || bill.confirmed ? t('form.yes') : t('form.no')),
	};

	const handleExport = (selectedKeys) =>
		buildXlsxExport({
			rows: visibleBills,
			fields: exportFields,
			selectedKeys,
			getters: exportGetters,
			sheetName: 'Gastos',
			filePrefix: 'gastos',
		});

	const renderRowActions = (bill) => (
		<RowActionsMenu
			items={[
				{ label: t('action.edit'), onClick: () => editBill(bill) },
				bill.comprobante?.fileLink && { label: t('action.viewReceipt'), onClick: () => verComprobante(bill) },
				!bill.paid && !bill.confirmed && { label: t('action.confirmPayment'), onClick: () => confirmPayment(bill) },
				{ label: t('action.delete'), onClick: () => deletePaymentHandler(bill), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<div className='tw:flex tw:justify-end tw:gap-2'>
				<Button size='sm' icon={<FaFileExcel />} onClick={() => setExportOpen(true)} disabled={visibleBills.length === 0}>
					{t('action.export')}
				</Button>
				{canCreate() && (
					<Button size='sm' variant='primary' onClick={newBillHandler}>
						{t('action.add')} {t('entity.bill')}
					</Button>
				)}
			</div>

			{isPeriodicTab ? (
				<PeriodicBillGroups
					groups={periodicGroups}
					asset={asset}
					loading={loading}
					error={error}
					onEdit={editBill}
					onEditTemplate={editTemplate}
					onDelete={deletePaymentHandler}
					onSeeAttachment={verComprobante}
					onConfirmPayment={confirmPayment}
					onRefetch={onRefetch}
				/>
			) : (
				<>
					<StatStrip stats={stats} loading={loading} skeletonCount={3} />
					<Section title={t('title.billsEventual')}>
						<DataTable
							columns={columns}
							rows={visibleBills}
							loading={loading}
							error={error}
							sortColumn={sortColumn}
							direction={direction}
							onSort={onSort}
							onRowClick={(bill) => navigate(`/AdminScreen/bills/bill/${bill._id}`)}
							rowActions={renderRowActions}
							emptyMessage={t('msg.noData')}
						/>
					</Section>
				</>
			)}

			<ExportModal
				show={exportOpen}
				onClose={() => setExportOpen(false)}
				count={visibleBills.length}
				fields={exportFields}
				subtitle={t('entity.bill.plural')}
				onExport={handleExport}
			/>

			<ConfirmationModal
				show={showConfirm}
				handleClose={handleCloseConfirm}
				handleConfirm={confirmDelete}
				title={t('title.deleteBill')}
				description={t('msg.confirmDeleteBill')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
				checkboxLabel={
					billToDelete && billToDelete.billTemplate && billToDelete.billTemplate.active &&
					templateHasAlreadyBillsGenerated(billToDelete) ? t('msg.stopFutureBillGeneration') : ''
				}
				checkboxDefaultValue={true}
			/>
			{showConfirmPaymentModal && billToConfirm && (
				<ConfirmPayment
					id={billToConfirm._id}
					onClose={closeConfirmPaymentModal}
					onSaved={handlePaymentConfirmed}
				/>
			)}
		</div>
	);
}

AssetBills.propTypes = {
	asset: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
	billType: PropTypes.oneOf(['eventual', 'periodic']),
	rows: PropTypes.array,
	loading: PropTypes.bool,
	error: PropTypes.string,
	onRefetch: PropTypes.func,
	returnToBase: PropTypes.string,
};

export default AssetBills;
